import { Box, Chip, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomViewFormConditionItem,
  filterValidCustomViewFilters,
} from 'src/graphql/entities/custom-view';
import {
  CustomViewOperator,
  customViewOperatorsMap,
  isFlagField,
  isUnary,
} from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { ConditionOperator } from 'src/graphql/types';
import { QueryOptionsValue } from 'src/pages/Settings/rules/use-rules-engine';
import { OperatorSelector } from './operator-selector';
import { ValueSelector } from './value-selector';

interface ChipItemProps {
  condition: CustomViewFormConditionItem;
  operators: readonly CustomViewOperator[] | undefined;
  optionsSource?: QueryOptionsValue;
  onDelete: (id: string) => void;
  onChange: (updated: CustomViewFormConditionItem) => void;
  autoValueSelector?: boolean;
  operatorDisplayOverrides?: Partial<Record<ConditionOperator, string>>;
}

export const ChipItem = ({
  condition,
  operators,
  optionsSource,
  onDelete,
  onChange,
  autoValueSelector,
  operatorDisplayOverrides,
}: ChipItemProps) => {
  const [valueOpen, setValueOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const applyDisplayOverride = useCallback(
    (op?: CustomViewOperator) => {
      const override = op && operatorDisplayOverrides?.[op.value];
      return override ? { ...op, display: override } : op;
    },
    [operatorDisplayOverrides],
  );

  const operator = applyDisplayOverride(
    condition.operator ? customViewOperatorsMap.get(condition.operator) : undefined,
  );

  const displayOperators = useMemo(
    () => operators?.map((op) => applyDisplayOverride(op)!),
    [operators, applyDisplayOverride],
  );

  const isEmptyValue = useMemo(() => {
    const v = condition.value;
    if (v === undefined || v === null || v === '') return true;
    return Array.isArray(v) ? v.length === 0 : false;
  }, [condition.value]);

  useEffect(() => {
    if (autoValueSelector && !isUnary(condition.operator) && isEmptyValue) {
      setValueOpen(true);
    }
  }, [autoValueSelector, condition.operator, isEmptyValue]);

  const handleOperatorChange = (op: CustomViewOperator) => {
    onChange({ ...condition, operator: op.value });
    if (isUnary(op.value) || isEmptyValue) {
      setValueOpen(true);
    }
  };

  const handleValueChange = (val: CustomViewFormConditionItem['value']) =>
    onChange({ ...condition, value: val });

  const handlePopoverToggle = (open: boolean) => {
    if (!filterValidCustomViewFilters([condition])?.length) {
      onDelete(condition.id!);
    }
    setValueOpen(open);
  };

  return (
    <Chip
      sx={{
        backgroundColor: (theme) => theme.palette.custom.OV.sys.tertiaryContainer,
        borderRadius: '0.4rem',
        maxWidth: '100%',
        '& .MuiChip-label': { minWidth: 0, overflow: 'hidden' },
      }}
      label={
        <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxWidth: '200px',
              flexShrink: 0,
            }}
          >
            {condition.field?.label}
          </Box>

          {!isFlagField(condition.field) && (
            <OperatorSelector
              operator={operator}
              operators={displayOperators}
              onChange={handleOperatorChange}
            />
          )}

          <div ref={anchorRef} />

          {!isUnary(condition.operator) && (
            <ValueSelector
              condition={condition}
              onChange={handleValueChange}
              anchorEl={anchorRef.current}
              open={valueOpen}
              setOpen={handlePopoverToggle}
              optionsSource={optionsSource}
            />
          )}
        </Stack>
      }
      onDelete={() => onDelete(condition.id!)}
    />
  );
};
